import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import {
  errorToastClassName,
  sonnerToast,
  successToastClassName,
  appToast as toast,
} from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import SearchEmpty from "@images/empty/search_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { useProgramSasRequests } from "@modules/programs/programSas/hooks/useProgramSasRequests"
import { useProgramSasRequestActions } from "@modules/programs/programSas/hooks/useProgramSasRequestsActions"
import { useProgramSasRequestTotal } from "@modules/programs/programSas/hooks/useProgramSasRequestTotal"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoaderCircle, Search, X } from "lucide-react"
import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react"

type ProgramSasPaymentChoicesProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSasPaymentChoices({ programId, className, ...props }: ProgramSasPaymentChoicesProps) {
  const confirmPopup = useConfirmPopup()
  const [selectedItems, setSelectedItems] = useState<ProgramSubscriberEntity[]>([])
  const [unSelectedItems, setUnSelectedItems] = useState<ProgramSubscriberEntity[]>([])
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const [searchTerms, setsearchTerms] = useState<string>("")
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const {
    data: { content: subscribers },
    isLoading: isLoadingSubscribers,
    isFetching: isFetchingSubscribers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramSasRequests(programId, ProgramSubscriptionStatus.PENDING_REQUEST, debouncedSearchTerms)
  const { data: sasRequestTotal } = useProgramSasRequestTotal(programId)
  const { acceptProgramSasSubscriptions, isAcceptProgramSasSubscriptionsPending, acceptAllProgramSasSubscriptions } =
    useProgramSasRequestActions(programId)
  const { navigateToProgramPriceEdition } = useProgramSidebar()

  const handleValidateAll = () => {
    confirmPopup.show({
      title: word("programSas.popup.allRequestsAccepted.title"),
      message: word("programSas.popup.allRequestsAccepted.message"),
      accept: () => {
        return Promise.resolve(
          void (() => {
            sonnerToast.promise(
              acceptAllProgramSasSubscriptions()
                .then(() => {
                  navigateToProgramPriceEdition(programId, true)
                  toast.success(word("programSas.toast.allRequestsAccepted.success"), {
                    classNames: successToastClassName(),
                  })
                })
                .catch(() => {
                  toast.error(word("programSas.toast.allRequestsAccepted.error"), {
                    classNames: errorToastClassName(),
                  })
                }),
            )
          }),
        )
      },
      footerProps: {
        confirmText: word("global.allCheck"),
      },
    })
  }

  const handleValidate = () => {
    if (selectedItems.length > 0) {
      sonnerToast.promise(
        acceptProgramSasSubscriptions(selectedItems.map((subscriber) => subscriber.subscriptionId))
          .then(() => {
            navigateToProgramPriceEdition(programId, true)
            toast.success(word("programSas.toast.allRequestsAccepted.success"), {
              classNames: successToastClassName(),
            })
          })
          .catch(() => {
            toast.error(word("programSas.toast.allRequestsAccepted.error"), {
              classNames: errorToastClassName(),
            })
          }),
      )
    } else {
      navigateToProgramPriceEdition(programId, true)
    }
  }

  const onSelect = (subscriber: ProgramSubscriberEntity) => {
    if (selectedItems.find((selectedItem) => selectedItem.id === subscriber.id)) {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== subscriber.id))
    } else {
      setSelectedItems([...selectedItems, subscriber])
    }
  }

  const onSelectedItems = useEffectEvent(() => {
    setUnSelectedItems(
      subscribers.filter((subscriber) => !selectedItems.find((selectedItem) => selectedItem.id === subscriber.id)),
    )
  })

  useEffect(() => {
    onSelectedItems()
  }, [subscribers, selectedItems])

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn("flex h-full grow flex-col gap-3 px-5 pt-18", className)} {...props}>
      <div className="flex flex-col gap-1">
        <Text fontStyle="TL">{word("program.price.membershipPrice")}</Text>
        <Text fontStyle="BS">{word("program.price.membershipPriceDescription")}</Text>
      </div>
      <div className="flex grow flex-col gap-3">
        <Text fontStyle="TS" className="my-3 text-center">
          {word("program.sasChoice.free.title")}
        </Text>

        <div className="flex flex-row items-baseline justify-end">
          <Text fontStyle="BS">{`${selectedItems.length} / ${sasRequestTotal.pendingRequestTotal} ${word("global.selected", { count: selectedItems.length === 0 ? 1 : selectedItems.length })}`}</Text>
          <Button variant="underline" className="px-3 py-2" onClick={handleValidateAll}>
            {word("global.allCheck")}
          </Button>
        </div>

        <div className="flex flex-col gap-4">
          <InputGroup className="mb-6">
            <InputGroupInput
              placeholder={word("program.subscribersList.search.placeholder")}
              onChange={(e) => setsearchTerms(e.currentTarget.value)}
              value={searchTerms}
            />
            <InputGroupAddon align="inline-start">
              <Search />
            </InputGroupAddon>
            {searchTerms && (
              <InputGroupAddon align="inline-end">
                <X className="size-4 cursor-pointer" onClick={() => setsearchTerms("")} />
              </InputGroupAddon>
            )}
          </InputGroup>

          {selectedItems.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className="flex w-full items-baseline gap-3">
                <Text fontStyle="BS" className="text-textDefault-heading">
                  {word("program.sasChoice.selected.title")}
                </Text>
                <div className="border-top-1 border-neutralColor-200 flex grow" />
              </div>
              <div className="flex max-h-80 grow flex-col gap-3 overflow-y-auto">
                {selectedItems.map((subscriber) => (
                  <SelectedTemplate
                    key={"selected-" + subscriber.id}
                    subscriber={subscriber}
                    onSelect={() => onSelect(subscriber)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className={`flex flex-col gap-3 ${unSelectedItems.length > 0 ? "gap-3" : "gap-0"}`}>
            {unSelectedItems.length > 0 ? (
              <div className="flex w-full items-baseline gap-3">
                <Text fontStyle="BS" className="font-semibold text-textDefault-caption">
                  {word("program.sasChoice.unselected.title")}
                </Text>
                <div className="flex grow border-t border-neutral-200" />
              </div>
            ) : null}
            {isLoadingSubscribers ? (
              <LoadingSpinner />
            ) : unSelectedItems.length > 0 ? (
              <div className="flex flex-col gap-3">
                {unSelectedItems.map((subscriber) => (
                  <div className="flex w-full items-center gap-3 pr-2" key={subscriber.id}>
                    <ProfileAvatar
                      className="size-12"
                      profile={{ avatar: subscriber.avatar ?? null, username: subscriber.firstname ?? "" }}
                    />
                    <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                      <Text
                        fontStyle="LL"
                        className="truncate"
                      >{`${subscriber.firstname} ${subscriber.lastname}`}</Text>
                      <Text fontStyle="BS" className="truncate text-textDefault-caption">
                        {`@${subscriber.username}`}
                      </Text>
                    </div>
                    <Checkbox
                      id={`user-${subscriber.id}`}
                      checked={!!selectedItems.find((selectedItem) => selectedItem.id === subscriber.id)}
                      onCheckedChange={() => onSelect(subscriber)}
                      className="size-5.5 shrink-0"
                    />
                  </div>
                ))}
                {(hasNextPage || isFetchingNextPage) && (
                  <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
                    <Text fontStyle="BM">{word("global.loading")}</Text>
                  </div>
                )}
              </div>
            ) : unSelectedItems.length === 0 && !isFetchingSubscribers && searchTerms.length > 0 ? (
              <EmptyView
                image={{ src: SearchEmpty }}
                title={word("explore.search.no_result.title", { searchTerms })}
                description={word("explore.search.no_result.message")}
                flexGrow={false}
                textAlign="left"
                imagePosition="after"
                className="flex w-full bg-surface-background px-12"
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 flex bg-surface-background">
        <Button
          variant="primary"
          size="lg"
          className="my-5 w-full"
          onClick={handleValidate}
          disabled={isAcceptProgramSasSubscriptionsPending}
          isLoading={isAcceptProgramSasSubscriptionsPending}
        >
          {isAcceptProgramSasSubscriptionsPending && <LoaderCircle className="animate-spin" />}
          {word("global.check")}
        </Button>
      </div>
    </div>
  )
}

type SelectedTemplateProps = {
  subscriber: ProgramSubscriberEntity
  onSelect: (subscriber: ProgramSubscriberEntity) => void
  className?: string
} & React.ComponentProps<"div">

function SelectedTemplate({ subscriber, onSelect, className, ...props }: SelectedTemplateProps) {
  return (
    <div className={cn("flex w-full items-center gap-3 pr-2", className)} key={subscriber.id} {...props}>
      <ProfileAvatar
        className="size-12"
        profile={{ avatar: subscriber.avatar ?? null, username: subscriber.firstname ?? "" }}
      />
      <div className="flex flex-1 flex-col gap-1 overflow-hidden">
        <Text fontStyle="LL" className="truncate">{`${subscriber.firstname} ${subscriber.lastname}`}</Text>
        <Text fontStyle="BS" className="truncate text-textDefault-caption">
          {`@${subscriber.username}`}
        </Text>
      </div>
      <Checkbox
        id={`user-${subscriber.id}`}
        checked={true}
        onCheckedChange={() => onSelect(subscriber)}
        className="size-5.5 shrink-0"
      />
    </div>
  )
}
